import { gql } from './client';
import { emit } from './events';
import { getUserSub } from '../auth';
import { CONFIG } from '../config';

// The workflow takes the account to close from the signed-in user on the event, never from the payload.
export function emitCloseMyPlanningAccount(connectionId?: string): Promise<string> {
  return emit('CloseMyPlanningAccount', getUserSub() ?? 'me', {}, connectionId);
}

// Whether the person holds any Planning role. Closing an account revokes them all, so false means closed (or never set up).
export async function hasPlanningRoles(userId: string): Promise<boolean> {
  const d = await gql<{ userPermissions: { permissionKey: string }[] | null }>(
    'query EPAccess($userId: String!, $appId: String!) { userPermissions(userId: $userId, applicationId: $appId) { permissionKey } }',
    { userId, appId: CONFIG.applicationId },
  );
  return (d.userPermissions ?? []).length > 0;
}

export interface AccountSummary {
  forename: string | null;
  surname: string | null;
  createdAt: string | null;
  termsAcceptedVersion: string | null;
  termsAcceptedAt: string | null;
}

export async function fetchAccountSummary(userId: string): Promise<AccountSummary> {
  const d = await gql<{ elevationsPlanningUserProfile: AccountSummary | null }>(
    'query MyAccount($id: ID!) { elevationsPlanningUserProfile(id: $id) { forename surname createdAt termsAcceptedVersion termsAcceptedAt } }',
    { id: userId },
  );
  const p = d.elevationsPlanningUserProfile;
  return {
    forename: p?.forename ?? null, surname: p?.surname ?? null, createdAt: p?.createdAt ?? null,
    termsAcceptedVersion: p?.termsAcceptedVersion ?? null, termsAcceptedAt: p?.termsAcceptedAt ?? null,
  };
}

// As with closing, the workflow changes the signed-in user's own name, never one named in the payload.
export function emitUpdateMyPlanningName(forename: string, surname: string, connectionId?: string): Promise<string> {
  return emit('UpdateMyPlanningName', getUserSub() ?? 'me', { forename, surname }, connectionId);
}

// The Terms version the person last accepted, or null if none. Throws when it can't be read.
export async function fetchTermsAcceptedVersion(userId: string): Promise<string | null> {
  const d = await gql<{ elevationsPlanningUserProfile: { termsAcceptedVersion: string | null } | null }>(
    'query MyTerms($id: ID!) { elevationsPlanningUserProfile(id: $id) { termsAcceptedVersion } }',
    { id: userId },
  );
  return d.elevationsPlanningUserProfile?.termsAcceptedVersion ?? null;
}

/** Which kind of check the person last chose on the Assess page; Application when they never chose. */
export async function fetchAssessMode(userId: string): Promise<'Application' | 'PreApplication'> {
  const d = await gql<{ elevationsPlanningUserProfile: { assessMode: string | null } | null }>(
    'query MyAssessMode($id: ID!) { elevationsPlanningUserProfile(id: $id) { assessMode } }',
    { id: userId },
  );
  return d.elevationsPlanningUserProfile?.assessMode === 'PreApplication' ? 'PreApplication' : 'Application';
}

// Recorded against the signed-in user by the workflow, never an id from the payload.
export function emitSetMyAssessMode(assessMode: 'Application' | 'PreApplication'): Promise<string> {
  return emit('SetMyPlanningAssessMode', getUserSub() ?? 'me', { assessMode });
}

// Recorded against the signed-in user by the workflow, never an id from the payload.
export function emitAcceptPlanningTerms(version: string, connectionId?: string): Promise<string> {
  return emit('AcceptPlanningTerms', getUserSub() ?? 'me', { version }, connectionId);
}

// Keeps the Users page's administrator flag in step with the person's real role (a role given by the assign-roles
// script, say). Fires the sync only when the two differ; the workflow re-reads the role itself.
export async function syncAdministratorFlag(userId: string): Promise<void> {
  const d = await gql<{
    userPermissions: { permissionKey: string }[] | null;
    elevationsPlanningUserProfile: { administrator: boolean | null } | null;
  }>(
    `query MyAdminFlag($userId: String!, $id: ID!, $appId: String!) {
      userPermissions(userId: $userId, applicationId: $appId, permissionKey: "users:write") { permissionKey }
      elevationsPlanningUserProfile(id: $id) { administrator }
    }`,
    { userId, id: userId, appId: CONFIG.applicationId },
  );
  const isAdmin = (d.userPermissions ?? []).length > 0;
  if (isAdmin !== (d.elevationsPlanningUserProfile?.administrator === true)) {
    await emit('SyncMyPlanningAdministrator', userId, {});
  }
}
